import { memo, useMemo } from 'react';

import { useFilters } from 'hooks/useFilters';

import {
  useGetRetentionPeriodsQuery,
  useGetTopGamesByRetentionQuery,
} from 'store/api/configService/analyticsController';

import { appendTopGameColors } from 'utils/analytics';

import Loader from 'components/Loader/Loader';
import Button from 'components/UI/Button/Button';
import ToggleFilters from 'components/UI/ToggleFilters/ToggleFilters';
import Typography from 'components/UI/Typography/Typography';
import Filters from 'components/pages/Analytics/Filters/Filters';
import GameBars from 'components/pages/Analytics/GameBars/GameBars';
import GamesList from 'components/pages/Analytics/GamesByRetention/GamesList/GamesList';
import Section from 'components/pages/Analytics/Layout/Section/Section';
import SectionContent from 'components/pages/Analytics/Layout/SectionContent/SectionContent';
import SectionHeader from 'components/pages/Analytics/Layout/SectionHeader/SectionHeader';

import { LIMITS, OPTIONS, TOP_GAME_COLORS } from 'components/config/analytics';

import { ReactComponent as DownloadIcon } from 'images/Icons/Download.svg';

import styles from './GamesByRetention.module.scss';

const GamesByRetention = () => {
  const {
    filters: { type, ...filters },
    handleChange,
  } = useFilters({
    type: OPTIONS.gameTypes[0].value,
    period: 'THREE_DAYS',
  });

  const { data, isLoading } = useGetTopGamesByRetentionQuery({
    category: type,
    period: filters.period,
    limit: LIMITS.byRetention,
  });

  const { data: retentionPeriods, isLoading: retentionPeriodsLoading } =
    useGetRetentionPeriodsQuery();

  const filtersOptions = useMemo(
    () => [
      {
        title: 'Choose period',
        label: 'period',
        value: filters.period,
        items: retentionPeriods,
        handleSelect: handleChange,
        loading: retentionPeriodsLoading,
      },
    ],
    [filters, handleChange, retentionPeriods, retentionPeriodsLoading],
  );

  const games = useMemo(
    () => appendTopGameColors(data, TOP_GAME_COLORS.byRetention),
    [data],
  );

  return (
    <Section className={styles.section}>
      <SectionHeader
        headings={
          <>
            <Typography type="heading-md" weight="medium" uppercase>
              {LIMITS.byRetention} Best games by retention
            </Typography>
            <Typography type="text-lg" color="grey">
              This dashboard highlights the top-performing games with the
              highest retention rates, both in the short and long term
            </Typography>
          </>
        }
        filters={
          <>
            <ToggleFilters
              name="type"
              value={type}
              options={OPTIONS.gameTypes}
              onChange={handleChange}
            />
            <Filters options={filtersOptions} />
            <Button
              variant="icon"
              startIcon={<DownloadIcon width={24} height={24} />}
            >
              XLS
            </Button>
          </>
        }
      />
      {!isLoading ? (
        <SectionContent type="bars">
          <div className={styles.listWrapper}>
            <GamesList games={games} />
            <GameBars type="horizontal" games={games} />
          </div>
          <Typography type="text-sm" color="grey" align="right">
            01 Jan 2024 - 31 Mar 2024 data is shown
          </Typography>
        </SectionContent>
      ) : (
        <Loader />
      )}
    </Section>
  );
};

export default memo(GamesByRetention);
